import React from "react";

interface AvatarProps {
  // The user's profile image URL. When empty/missing, renders the user's
  // first-name initial in place of the image.
  src?: string | null;
  // The user's display name. Drives both the alt text and the initial
  // letter shown when no image is available.
  name?: string | null;
  // Caller-provided size/shape classes (e.g. "sellerInfo__img--small").
  // Applied identically whether we render <img> or the initials <span>,
  // so existing avatar size/border-radius/background rules keep working.
  className?: string;
  style?: React.CSSProperties;
  alt?: string;
  onClick?: (e: React.MouseEvent) => void;
}

// Take the first character of the trimmed name. codePointAt + fromCodePoint
// keeps multi-byte characters (emoji, accented letters) intact instead of
// slicing them into half-surrogates.
const initialOf = (name?: string | null): string => {
  const trimmed = name?.trim() ?? "";
  if (!trimmed) return "?";
  const cp = trimmed.codePointAt(0);
  return cp ? String.fromCodePoint(cp).toUpperCase() : "?";
};

const Avatar: React.FC<AvatarProps> = ({
  src,
  name,
  className = "",
  style,
  alt,
  onClick,
}) => {
  const hasSrc = typeof src === "string" && src.trim() !== "";

  if (hasSrc) {
    return (
      <img
        src={src as string}
        alt={alt ?? name ?? ""}
        className={className}
        style={style}
        onClick={onClick}
      />
    );
  }

  // Initials fallback. The letter is rendered as SVG <text> inside a
  // viewBox-driven <svg> so it auto-scales to whatever pixel size the
  // host avatar class picks (40px circles, 120px hero avatars, etc.)
  // without needing CSS container queries — works in every browser.
  return (
    <span
      role="img"
      aria-label={alt || name || "User avatar"}
      className={`avatarInitial ${className}`}
      style={style}
      onClick={onClick}
    >
      <svg
        className="avatarInitial__svg"
        viewBox="0 0 40 40"
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
        focusable="false"
      >
        <text
          x="20"
          y="20"
          textAnchor="middle"
          dominantBaseline="central"
          className="avatarInitial__letter"
        >
          {initialOf(name)}
        </text>
      </svg>
    </span>
  );
};

export default Avatar;
